import {useEffect} from "react";
import {useDispatch, useSelector} from "react-redux";
import {AppDispatch} from "@core/store";
import {api} from "@core/api";
import {logout, setAccessToken} from "@core/store/slices/authSlice";
import {showMessage} from "@core/store/slices/snackbarSlice";
import {translate} from "@util";
import {clearApp} from "@core/store/slices/appSlice";
import {Selectors} from "@core/store/selectors";

// Costanti di configurazione
const REFRESH_INTERVAL_MS = 10000; // 10 secondi
const TOKEN_REFRESH_THRESHOLD_MS = 120000; // Refresh quando restano 2 minuti

/**
 * Decodifica un JWT e restituisce il payload
 */
const decodeJWT = (token: string): {exp?: number} | null => {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;

    const decoded = JSON.parse(atob(parts[1]));
    return decoded;
  } catch {
    return null;
  }
};

/**
 * Controlla se il token è scaduto o sta per scadere
 */
const isTokenExpiredOrExpiring = (
  token: string,
  thresholdMs = TOKEN_REFRESH_THRESHOLD_MS,
): boolean => {
  const payload = decodeJWT(token);
  if (!payload?.exp) return true;

  const expirationTime = payload.exp * 1000; // Converti da secondi a millisecondi
  const now = Date.now();

  return now >= expirationTime - thresholdMs;
};

export const useTokenRefresher = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [triggerRefreshToken] = api.useLazyRefreshTokenQuery();
  const accessToken = useSelector(Selectors.accessToken);

  useEffect(() => {
    const interval: NodeJS.Timeout | null = setInterval(() => {
      if (!accessToken) {
        return;
      }

      // Controlla se il token di accesso è scaduto o sta per scadere
      if (isTokenExpiredOrExpiring(accessToken)) {
        triggerRefreshToken()
          .unwrap()
          .then(response => {
            dispatch(setAccessToken(response.accessToken));
          })
          .catch(() => {
            dispatch(
              showMessage({
                severity: "error",
                text: translate("error.sessionExpired"),
              }),
            );
            dispatch(clearApp());
            dispatch(logout());
          });
      }
    }, REFRESH_INTERVAL_MS);

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [dispatch, accessToken]);
};
